import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import StatueProductCard from '../components/StatueProductCard';

const StatuesGallery = () => {
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [statueProducts, setStatueProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const titleRef = useRef(null);
  const [isTitleVisible, setIsTitleVisible] = useState(false);

  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/products/statues`);
        if (response.ok) {
          const data = await response.json();
          setStatueProducts(data);
        }
      } catch (error) {
        console.error('Failed to fetch statues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [apiUrl]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsTitleVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12 flex items-center justify-center">
        <div className="text-[hsl(45,100%,70%)] text-2xl">Loading statues...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Back Button - Aligned with logo position */}
        <div className="mb-8 flex justify-center relative z-[60]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>
        </div>

        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Handcrafted <span className="text-[hsl(45,100%,70%)]">Masterpieces</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Unique pieces - every sculpture is adorned with delicate rhinestones and created only once, 
            to have an exclusive place in your living space.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="wait">
            {statueProducts.map((product, index) => {
              const currentIndex = statueProducts.findIndex(p => p.id === expandedProductId);
              const handlePreviousProduct = () => {
                const prevIndex = currentIndex === 0 ? statueProducts.length - 1 : currentIndex - 1;
                setExpandedProductId(statueProducts[prevIndex].id);
              };
              const handleNextProduct = () => {
                const nextIndex = (currentIndex + 1) % statueProducts.length;
                setExpandedProductId(statueProducts[nextIndex].id);
              };

              return (
                <StatueProductCard 
                  key={product.id} 
                  product={product} 
                  index={index}
                  onExpand={setExpandedProductId}
                  isExpanded={expandedProductId === product.id}
                  onClose={() => setExpandedProductId(null)}
                  onPreviousProduct={handlePreviousProduct}
                  onNextProduct={handleNextProduct}
                  hasMultipleProducts={statueProducts.length > 1}
                />
              );
            })}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {statueProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No statues available</p>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-20 bg-gradient-to-r from-[hsl(45,100%,70%)]/10 to-transparent rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Handcrafted Excellence
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed max-w-4xl">
            Each sculpture is meticulously hand-crafted by our skilled artisans. 
            From intricate details to bold contemporary designs, every piece 
            represents hours of dedicated craftsmanship. Our sculptures are not just decorative objects – 
            they are sculptural art pieces that transform your living spaces into 
            galleries of artistic expression and timeless beauty.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StatuesGallery;
